import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { OfferBilan } from './prevention-offers.service';

export interface ActivityItem {
  opportunityId: string;
  offerName: string;
  price: number | null;
  currency: string | null;
  interestAt: string;
  /** Refonte résultat (2026-10-16) : `CONTEXTUAL_OFFER` = « Ça m'intéresse » sur l'offre complémentaire du laboratoire. */
  source?: 'RECOMMENDATION' | 'CONTEXTUAL_OFFER';
  /** V331 : identité déclarée par le patient (site qui la demande + consentement), sinon absente. À vérifier avant usage. */
  patientLastName?: string | null;
  patientFirstName?: string | null;
  patientBirthDate?: string | null;
}

export interface ActivityPage {
  items: ActivityItem[];
  page: number;
  size: number;
  totalElements: number;
  hasMore: boolean;
}

export interface PreventionPerformance {
  interestedCount: number;
  opportunitiesValue: number;
  currency: string;
}

export interface PreventionFunnel {
  exposed: number;
  /** `null` (avec `insufficientData`) quand le backend retient les totaux depuis toujours : jamais 0. */
  started: number | null;
  completed: number | null;
  recommendationsDisplayed: number;
  /** Dénominateur robuste du taux d'intérêt (fiabilisé 2026-08-26) : sessions ayant DISPLAYED ou une décision (INTERESTED/LATER/DECLINED) -- jamais recommendationsDisplayed seul, qui peut sous-compter (tracking frontend best-effort) et faire dépasser 100%. */
  recommendationsReachedDecision: number;
  interested: number;
  insufficientData?: boolean;
}

export type PatientOutcomeKind = 'RECOMMENDATION' | 'INSUFFICIENT_INFORMATION' | 'ALREADY_FOLLOWED' | 'NO_RECOMMENDATION';

export interface OutcomeCount {
  kind: PatientOutcomeKind;
  count: number;
}

export interface AbandonmentBucket {
  /** Code du module ; `SENSITIVE_MODULE` = bucket GLOBAL de tous les modules sensibles (backend : jamais le code réel). */
  moduleCode: string;
  /** Position brute ; `null` pour le bucket sensible global (jamais de pseudo-position : aucun repérage possible du module). */
  position: number | null;
  /** Rang humain 1-indexé de la question (1ʳᵉ, 2ᵉ...) -- null pour "avant la 1ʳᵉ question" ou une position sans question active correspondante. Ne jamais afficher `position` brute au staff (pas de 10 en base). */
  rank: number | null;
  /** Libellé réel de la question (français de base). Null dans les mêmes cas que `rank`, ET systématiquement null si le module est sensible (backend fail-closed) -- utiliser `rank` comme repli dans ce cas, jamais un texte manquant traité comme une erreur. */
  questionText: string | null;
  /** `null` quand `insufficientData` (petite cohorte : l'effectif exact n'est jamais exposé). */
  count: number | null;
  /** Effectif masqué (seuil de petite cohorte ou suppression complémentaire) : afficher « données insuffisantes ». */
  insufficientData: boolean;
}

export interface FunnelTimelinePoint {
  date: string;
  started: number;
  completed: number;
}

/** Sous-page "Parcours patient" -- population de référence = cohorte des sessions démarrées dans la période, voir backend FunnelAnalytics. */
export interface FunnelAnalytics {
  days: number;
  periodStart: string;
  periodEnd: string;
  exposed: number;
  /** `null` (avec `insufficientData`) quand le backend retient les totaux : afficher « données insuffisantes », jamais 0 ni NaN. */
  started: number | null;
  completed: number | null;
  completionRate: number | null;
  recommendationsDisplayed: number;
  recommendationReached: number;
  interested: number;
  interestRate: number;
  /** `null` quand les totaux sont retenus (leur somme vaut exactement `completed`). */
  outcomes: OutcomeCount[] | null;
  abandonmentQuestionnaireVersionId: string;
  abandonment: AbandonmentBucket[];
  /** `null` quand les totaux sont retenus (points journaliers started/completed). */
  timeline: FunnelTimelinePoint[] | null;
  /** Totaux du funnel retenus pour protéger un effectif masqué : même sémantique que `insufficientData` de la couverture. */
  insufficientData: boolean;
  /** Refonte résultat (2026-10-16) -- sessions « J'y repenserai plus tard ». */
  later?: number;
  /** Dont « Je fais ce bilan » ensuite. */
  laterConverted?: number;
  /** « Non merci » (y compris après un « plus tard »). */
  declined?: number;
  /** Détail du bilan ouvert avant la décision, ou intérêt : une fois par session. */
  engaged?: number;
  /** « Ça m'intéresse » sur l'offre complémentaire (jamais compté dans `interested`). */
  contextualInterested?: number;
  /** Intérêts par écran de décision : RESULT, OFFER_DETAIL, HOME_CARD, UNKNOWN (client antérieur). */
  interestedByOrigin?: Record<string, number>;
}

export interface OfferPerformance {
  offerId: string;
  offerName: string;
  recommendationsDisplayed: number;
  recommendationReached: number;
  interested: number;
  interestRate: number;
  opportunities: number;
  opportunityValue: number;
}

/** Sous-page "Performance & impact" -- jamais "Réalisés"/"CA généré"/"ROI" (STAFF_CONFIRMATION dormant). */
export interface PerformanceAnalytics {
  days: number;
  periodStart: string;
  periodEnd: string;
  opportunities: number;
  opportunityValue: number;
  currency: string;
  interestRate: number;
  offers: OfferPerformance[];
  /** Refonte résultat -- « dont offres complémentaires » (inclus dans `opportunities`/`opportunityValue`). */
  contextualOpportunities?: number;
  contextualOpportunityValue?: number;
}

export interface OfferTimelinePoint {
  date: string;
  opportunities: number;
  opportunityValue: number;
}

export interface OfferPerformanceDetail {
  offerId: string;
  offerName: string;
  days: number;
  periodStart: string;
  periodEnd: string;
  recommendationsDisplayed: number;
  recommendationReached: number;
  interested: number;
  interestRate: number;
  opportunities: number;
  opportunityValue: number;
  currency: string;
  timeline: OfferTimelinePoint[];
}

/**
 * Une Offer du catalogue vue depuis UN établissement, bundle-aware (Lot 9.0-E.E.G) -- reflète
 * `EstablishmentOfferCatalogView` (backend, `GET .../catalog/offers`). Liste PLATE, jamais groupée
 * par famille (une Offer peut couvrir plusieurs familles via ses bilans). `price`/`currency`/
 * `immediateFeasibilityDefault`/`conditions`/`patientContactRole` restent `null` tant que l'offre
 * n'est pas active sur CE site. `patientContactRole` est l'override BRUT, nullable -- `null` signifie
 * "pas de valeur spécifique à cette offre, utiliser le défaut du site" (`EstablishmentContact`),
 * résolu côté frontend, jamais par le backend (vérifié dans le javadoc de la vue backend avant
 * codage).
 */
export interface EstablishmentOfferCatalog {
  offerId: string;
  name: string;
  description: string | null;
  bilans: OfferBilan[];
  activeForEstablishment: boolean;
  price: number | null;
  currency: string | null;
  immediateFeasibilityDefault: boolean | null;
  conditions: string | null;
  patientContactRole: PatientContactRole | null;
  commercialPreference: boolean;
  /** Vrai si un AUTRE Offer actif sur ce site a une composition de bilans strictement identique -- seul cas où "Offre privilégiée sur ce site" a un sens. */
  hasDuplicateCoverage: boolean;
  hasPhoto: boolean;
  photoUpdatedAt: string | null;
}

/** Corps de `PUT .../catalog/offers/{offerId}` (mono-site) -- reflète `SetOfferAvailabilityRequest` backend, qui délègue à la même logique métier que le batch multi-site E.E.F (mêmes règles de validation). */
export interface SetEstablishmentOfferAvailabilityRequest {
  active: boolean;
  price: number | null;
  currency: string | null;
  immediateFeasibilityDefault: boolean;
  conditions: string | null;
  patientContactRole: PatientContactRole | null;
  commercialPreference: boolean;
}

/** Réponse de `setEstablishmentOfferAvailability` -- `CatalogOfferView` backend : identique à {@link EstablishmentOfferCatalog} SAUF `bilans[]`, jamais renvoyé par ce PUT (la composition ne change jamais depuis ce endpoint). */
export interface EstablishmentOfferAvailabilityResult {
  offerId: string;
  name: string;
  description: string | null;
  activeForEstablishment: boolean;
  price: number | null;
  currency: string | null;
  immediateFeasibilityDefault: boolean | null;
  conditions: string | null;
  patientContactRole: PatientContactRole | null;
}

/** Vocabulaire fermé exposé par le back-office (backend : `PreventionCatalogService.ALLOWED_CONTACT_ROLES`). */
export type PatientContactRole = 'ACCUEIL' | 'PRELEVEUR' | 'BIOLOGISTE' | 'AUTRE';

export interface EstablishmentContact {
  patientContactRole: PatientContactRole | null;
  /** « Après le prélèvement, le patient repasse par l'accueil » (V330) -- absent = false. N'agit que sur les bilans dont l'interlocuteur effectif est le préleveur. */
  patientReturnToReception?: boolean;
  /** V331 : identité du patient avec la demande, point fait sur place. Absent = `NONE`. */
  patientIdentityOnSite?: PatientIdentityMode;
  /** V331 : attestation (HDS, analyse d'impact, consentement validés) déjà donnée pour ce site. */
  identityAttested?: boolean;
}

export type PatientIdentityMode = 'NONE' | 'OPTIONAL' | 'REQUIRED';

// ───────────────────────── Pilotage multisites > Prévention (Lot 7, org-scope) ─────────────────────────

/** "Sites avec Prévention" = établissements ayant au moins une offre active aujourd'hui (choix assumé backend, voir PreventionGroupAnalyticsRepository). */
export interface GroupOverview {
  sitesWithPrevention: number;
  totalSites: number;
  opportunitiesValue: number;
  currency: string;
}

export interface GroupFunnelTimelinePoint {
  date: string;
  started: number;
  completed: number;
}

/** Funnel agrégé groupe -- mêmes définitions que FunnelAnalytics (site, Lot 6) : completionRate/interestRate = SUM/SUM sur le groupe, jamais une moyenne des taux par site. */
export interface GroupFunnelAnalytics {
  days: number;
  periodStart: string;
  periodEnd: string;
  /** Fuseau de référence utilisé pour borner "N derniers jours" (le plus fréquent parmi les établissements du groupe) -- voir rapport de lot pour le compromis assumé. */
  referenceTimezone: string;
  exposed: number;
  /** `null` (avec `insufficientData`) quand plus aucun site n'est additionnable : jamais 0. Un site aux totaux retenus est ÉCARTÉ de ces sommes (`excludedSiteCount`). */
  started: number | null;
  completed: number | null;
  completionRate: number | null;
  recommendationReached: number;
  interested: number;
  interestRate: number;
  timeline: GroupFunnelTimelinePoint[] | null;
  /** Nombre de sites écartés des totaux ci-dessus (totaux retenus par le backend) ; absent = 0. */
  excludedSiteCount?: number;
  /** Totaux retenus (aucun site additionnable) ; absent = false. */
  insufficientData?: boolean;
}

export interface GroupSiteRow {
  establishmentId: string;
  establishmentName: string;
  /** A au moins une offre active aujourd'hui -- même définition que GroupOverview.sitesWithPrevention. */
  active: boolean;
  /** `null` (avec `insufficientData`) : totaux du site retenus par le backend, jamais 0. */
  started: number | null;
  completionRate: number | null;
  interestRate: number;
  opportunityValue: number;
  insufficientData?: boolean;
}

export interface GroupOfferRow {
  offerId: string;
  offerName: string;
  siteCount: number;
  recommendationReached: number;
  interested: number;
  interestRate: number;
  opportunityValue: number;
}

export interface PreventionGroupDashboard {
  overview: GroupOverview;
  funnel: GroupFunnelAnalytics;
  sites: GroupSiteRow[];
  offers: GroupOfferRow[];
}

// ───────────────────────── Coverage Analytics (Lot V2A.4/V2A.5, "Besoins & couverture") ─────────────────────────

/** Vocabulaire fermé des états de couverture (backend `CoverageState`) -- `UNCLASSIFIED` inclus, jamais fondu dans une autre catégorie. `NO_OFFER_CONFIGURED_ON_SITE` (Lot V2A.5.1) : site n'ayant jamais configuré la moindre offre, séparé d'`UNCLASSIFIED`. */
export type CoverageState = 'NO_BILAN_MAPPING' | 'BILAN_AVAILABLE_NO_OFFER' | 'OFFER_UNAVAILABLE_ON_SITE' | 'UNCLASSIFIED' | 'NO_OFFER_CONFIGURED_ON_SITE';

export const AGE_BANDS: string[] = ['18-29', '30-39', '40-49', '50-59', '60-69', '70+'];

/** Bandeau qualité des données Coverage -- voir `CoverageDataQuality` (backend). Champs Coverage-only nullable (small-cohort), champs session/segmentation toujours visibles. */
export interface CoverageDataQuality {
  missingCoverageCount: number | null;
  coverageCompleteness: number | null;
  missingAgeBandCount: number;
  ageBandCompleteness: number;
  missingSessionContextCount: number | null;
  sessionContextCompleteness: number | null;
  smallCohortThreshold: number;
}

/** Une ligne de `needs[]` -- voir `NeedCoverageRow` (backend). Tous les champs sauf `needCode`/`needLabel`/`categoryCode`/`insufficientData` sont `null` quand `insufficientData=true` : ne jamais tenter de les reconstruire. */
export interface NeedCoverageRow {
  needCode: string;
  needLabel: string;
  categoryCode: string;
  detectedSessions: number | null;
  needDetectionRate: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  /** Nombre de sites (établissements) DISTINCTS ayant au moins une Coverage résolue non COVERED pour ce Need -- remplace le placeholder "Sites concernés" (complément 2026-09-06). */
  uncoveredSiteCount: number | null;
  needCoverageRate: number | null;
  uncoveredNeedRate: number | null;
  gapsByReason: Partial<Record<CoverageState, number>> | null;
  insufficientData: boolean;
}

/** Une ligne de `siteRows[]` -- voir `SiteCoverageRow` (backend). `detectedNeedCount` compte des INSTANCES `NeedEvaluation ELIGIBLE`, pas des sessions distinctes (contrairement à `NeedCoverageRow.detectedSessions`). */
export interface SiteCoverageRow {
  establishmentId: string;
  establishmentName: string;
  detectedNeedCount: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  needCoverageRate: number | null;
  uncoveredNeedRate: number | null;
  insufficientData: boolean;
}

/** `GET /api/v1/prevention/dashboard/needs` -- voir `GroupNeedCoverageView` (backend). Tous les agrégats Coverage globaux respectent la même logique `withholdTotals` : `null` ensemble, jamais partiellement. */
export interface GroupNeedCoverageView {
  days: number;
  periodStart: string;
  periodEnd: string;
  dataAvailableFrom: string | null;
  ageBandFilter: string | null;
  completedSessions: number;
  sessionsWithDetectedNeed: number | null;
  detectedNeedCount: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  needCoverageRate: number | null;
  uncoveredNeedRate: number | null;
  dataQuality: CoverageDataQuality;
  needs: NeedCoverageRow[];
  siteRows: SiteCoverageRow[];
}

/** `GET /api/v1/prevention/establishments/{id}/analytics/needs` -- voir `SiteNeedCoverageSummaryView` (backend), strict sous-ensemble du contrat Groupe, pas de filtre `ageBand` (pas de segmentation cohorte au niveau site). */
export interface SiteNeedCoverageSummaryView {
  days: number;
  periodStart: string;
  periodEnd: string;
  dataAvailableFrom: string | null;
  uncoveredNeedCount: number | null;
  needsAffectedCount: number | null;
  topGapReason: CoverageState | null;
  missingCoverageCount: number | null;
  coverageCompleteness: number | null;
  missingSessionContextCount: number | null;
  sessionContextCompleteness: number | null;
  insufficientData: boolean;
}

/** Une ligne de `sites[]` du Détail Need -- voir `NeedDetailSiteRow` (backend). Répartition PAR SITE d'un seul Need (contrairement à `SiteCoverageRow`, agrégat tous-Needs confondus). */
export interface NeedDetailSiteRow {
  establishmentId: string;
  establishmentName: string;
  detectedNeedCount: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  needCoverageRate: number | null;
  topGapReason: CoverageState | null;
  insufficientData: boolean;
}

/** Une ligne de `linkedOffers[]` du Détail Need -- vue CATALOGUE COURANT (offres actives dont au moins un bilan référence ce Need), jamais un snapshot historique. Voir `NeedDetailOfferRow` (backend). */
export interface NeedDetailOfferRow {
  offerId: string;
  offerName: string;
  activeSiteCount: number;
}

/** Bandeau qualité du Détail Need -- voir `NeedDetailDataQuality` (backend), volontairement réduit par rapport à `CoverageDataQuality` (pas d'ageBand/contexte session, sans objet scopé à un seul Need). */
export interface NeedDetailDataQuality {
  missingCoverageCount: number | null;
  coverageCompleteness: number | null;
  smallCohortThreshold: number;
}

/** `GET /api/v1/prevention/dashboard/needs/{needCode}` (Lot V2A.5-D) -- voir `NeedDetailView` (backend). `noData`/`insufficientData` mutuellement exclusifs ; ni l'un ni l'autre -> tous les champs numériques renseignés. Un `needCode` inconnu du catalogue lève un 404 (jamais un objet de ce type), voir `PreventionGroupService#getNeedDetail`. */
export interface NeedDetailView {
  days: number;
  periodStart: string;
  periodEnd: string;
  dataAvailableFrom: string | null;
  needCode: string;
  needLabel: string;
  categoryCode: string;
  noData: boolean;
  insufficientData: boolean;
  detectedSessions: number | null;
  needDetectionRate: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  needCoverageRate: number | null;
  uncoveredSiteCount: number | null;
  gapsByReason: Partial<Record<CoverageState, number>> | null;
  dataQuality: NeedDetailDataQuality | null;
  sites: NeedDetailSiteRow[];
  linkedOffers: NeedDetailOfferRow[];
}

// ───────────────────────── Opportunités de prévention unifiées (lot L6, endpoints L5) ─────────────────────────
// Types qui miroitent le contrat backend au nom près (`OpportunityDashboardView`, `OpportunityRow`,
// `OpportunityDetailView`, `OpportunitySiteRow`, `OpportunityLinkedOffer`). AUCUNE transformation de
// champ, AUCUN calcul ici : le front rend le contrat tel quel. En particulier :
//  - `subjectKey` est OPAQUE (`need:<code>` / `catalog:<code>`) : jamais parsée, jamais reconstruite,
//    renvoyée telle quelle à l'API ;
//  - `subjectOrigin` est présent dans le contrat mais n'est JAMAIS affiché ni utilisé (ni badge, ni filtre) ;
//  - les codes de raison et d'action sont fournis par le serveur : le front ne déduit jamais l'un de l'autre.

/** Vocabulaire de raisons de non-couverture du contrat unifié (`OpportunityGapReason`) -- `NOT_PRESENTABLE` (contenu en cours de validation) est propre aux sujets commerciaux. Une chaîne inconnue reste tolérée à la lecture (voir `OpportunityGapReasonCount#reason`). */
export type OpportunityGapReasonCode =
  'NO_BILAN_MAPPING' | 'BILAN_AVAILABLE_NO_OFFER' | 'OFFER_UNAVAILABLE_ON_SITE' | 'UNCLASSIFIED' | 'NO_OFFER_CONFIGURED_ON_SITE' | 'NOT_PRESENTABLE';

/** Action suggérée, décidée par le serveur (`OpportunityAction`). `NONE` = aucune action laboratoire possible : jamais de bouton. */
export type OpportunityActionCode = 'DEPLOYMENT' | 'CONFIGURE' | 'LIBRARY' | 'VERIFY' | 'NONE';

/** Fourni par l'API, JAMAIS rendu (règle V1 : la provenance d'un sujet n'est pas montrée au laboratoire). */
export type OpportunitySubjectOrigin = 'NEED_CLINICAL' | 'COMMERCIAL_PROFILE';

/** Une ligne de `opportunities[]`. Tous les champs numériques sont `null` quand `insufficientData=true` : ne jamais les reconstruire. `gapsByReason` : six clés uniformes, ou `null` pour une ligne masquée. */
export interface OpportunityRow {
  subjectKey: string;
  subjectLabel: string;
  familyCode: string;
  detectedSessions: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  coverageRate: number | null;
  uncoveredSiteCount: number | null;
  gapsByReason: Partial<Record<OpportunityGapReasonCode, number>> | null;
  insufficientData: boolean;
  subjectOrigin: OpportunitySubjectOrigin;
}

/**
 * `GET /api/v1/prevention/dashboard/opportunities` -- voir `OpportunityDashboardView` (backend).
 * `opportunityCount` est la SOMME des lignes (une session peut compter dans plusieurs sujets) ;
 * `distinctSessions` est exposé à côté, jamais à recalculer. `totalsWithheld=true` : tous les totaux
 * sont `null` ensemble (petite cohorte, ligne masquée...) ; `distinctSessions` peut en plus être
 * retenu seul. Aucun bloc `dataQuality` dans ce contrat.
 */
export interface OpportunityDashboardView {
  days: number;
  periodStart: string;
  periodEnd: string;
  dataAvailableFrom: string | null;
  ageBandFilter: string | null;
  completedSessions: number;
  globalCohortInsufficient: boolean;
  totalsWithheld: boolean;
  opportunityCount: number | null;
  distinctSessions: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  coverageRate: number | null;
  smallCohortThreshold: number;
  opportunities: OpportunityRow[];
}

/** Une raison de non-couverture du détail, avec l'action décidée par le serveur (déjà triée du plus fréquent au moins fréquent). */
export interface OpportunityGapReasonCount {
  reason: OpportunityGapReasonCode | string;
  count: number;
  action: OpportunityActionCode | null;
}

/** Une ligne de `sites[]` du détail d'une opportunité -- `topGapReason`/`topGapAction` sont fournis tels quels par le serveur (jamais un argmax recalculé). */
export interface OpportunitySiteRow {
  establishmentId: string;
  establishmentName: string;
  detectedSessions: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  coverageRate: number | null;
  topGapReason: OpportunityGapReasonCode | string | null;
  topGapAction: OpportunityActionCode | null;
  insufficientData: boolean;
}

/** Une ligne de `linkedOffers[]` -- offres actives de l'organisation rattachées au sujet (catalogue courant), sans prix ni filtre de rôle. */
export interface OpportunityLinkedOffer {
  offerId: string;
  offerName: string;
  activeSiteCount: number;
}

/** `GET /api/v1/prevention/dashboard/opportunities/{subjectKey}` -- voir `OpportunityDetailView` (backend). 404 si le sujet est inconnu, 400 si la clé est malformée. `noData`/`insufficientData` : sujet valide sans opportunité vs masqué (petite cohorte). */
export interface OpportunityDetailView {
  days: number;
  periodStart: string;
  periodEnd: string;
  dataAvailableFrom: string | null;
  subjectKey: string;
  subjectLabel: string;
  familyCode: string;
  subjectOrigin: OpportunitySubjectOrigin;
  noData: boolean;
  insufficientData: boolean;
  detectedSessions: number | null;
  resolvedCoverageCount: number | null;
  coveredCount: number | null;
  uncoveredCount: number | null;
  coverageRate: number | null;
  uncoveredSiteCount: number | null;
  gapsByReason: Partial<Record<OpportunityGapReasonCode, number>> | null;
  gapReasons: OpportunityGapReasonCount[];
  smallCohortThreshold: number;
  sites: OpportunitySiteRow[];
  linkedOffers: OpportunityLinkedOffer[];
}

// ───────────────────────── Patient Demand (Lot V2A.6-B/C, "Intérêt des patients") ─────────────────────────
// Vocabulaire interne "Patient Demand"/"topic"/"demand rate" JAMAIS montré à l'utilisateur -- le
// wording UI reste "Intérêt des patients"/"sujet"/"taux d'intérêt" (voir composant + i18n). Ces
// types miroitent le contrat backend au nom près, aucune transformation de champ ici.

/**
 * Une ligne de `topics[]` -- voir `TopicDemandRow` (backend). `insufficientData=true` masque TOUS
 * les champs numériques (y compris Opportunity/Intérêts adressables/€), jamais un compteur exact
 * puis caché côté UI.
 * <p>`opportunityCount`/`opportunityValue` (circuit questionnaire clinique, via `prevention.opportunities`)
 * restent sur le contrat pour compatibilité mais NE SONT PLUS affichés par `prevention-demand`
 * (lot "Contrat analytics Campagne", 2026-09-08) -- ils mélangeaient un circuit totalement
 * disjoint du clic campagne (voir rapport d'audit). `addressableInterestCount`/
 * `addressablePotentialValue` les remplacent sur cette page : EXCLUSIVEMENT `patient_demand_signals`
 * (source PREVENTION_FEED, offre réellement résolue au clic).
 */
export interface TopicDemandRow {
  topicCode: string;
  topicLabel: string;
  linkedNeedCode: string | null;
  exposedSessions: number | null;
  engagedSessions: number | null;
  interestedSessions: number | null;
  untraceableEngagedSessions: number | null;
  untraceableInterestedSessions: number | null;
  engagementRate: number | null;
  demandRate: number | null;
  opportunityCount: number | null;
  opportunityValue: number | null;
  realizedCount: number | null;
  insufficientData: boolean;
  denominatorReliable: boolean;
  offerAvailable: boolean | null;
  addressableInterestCount: number | null;
  addressablePotentialValue: number | null;
}

/**
 * `GET /api/v1/prevention/dashboard/demand` -- voir `PatientDemandDashboardView` (backend).
 * `exposedSessionsTotal`/`interestedSessionsTotal` sont l'UNION (jamais la somme) des sessions par
 * topic -- ne JAMAIS les recalculer depuis `topics[]` côté Angular.
 * <p>Small-cohort GLOBAL (Prevention First UX Phase 1) : `exposedSessionsTotal`/
 * `interestedSessionsTotal`/`interestRateTotal`/`topicsWithoutOfferCount` sont `null` (jamais une
 * valeur exacte, jamais `0`) dès que le total réel est sous `smallCohortThreshold` -- `exposedSessionsTotal
 * == null` est le signal unique de "cohorte globale insuffisante", ne jamais utiliser `=== 0` pour ça
 * (un total non nul mais < seuil est masqué de la même façon qu'un vrai zéro).
 */
export interface PatientDemandDashboardView {
  days: number;
  periodStart: string;
  periodEnd: string;
  demandDataAvailableFrom: string | null;
  completedSessions: number;
  exposedSessionsTotal: number | null;
  interestedSessionsTotal: number | null;
  interestRateTotal: number | null;
  topicsWithoutOfferCount: number | null;
  smallCohortThreshold: number;
  /** Depuis quand "Intérêts adressables"/"Potentiel identifié" sont mesurés (migration V242) -- DISTINCT de `demandDataAvailableFrom`, plus ancien. `null` si aucune offre n'a jamais été résolue pour cette organisation. */
  addressableInterestDataAvailableFrom: string | null;
  /** `COUNT(DISTINCT session_id)` calculé directement (Option A) -- jamais une somme des `topics[]`. Masqué (`null`) selon la même règle small-cohort globale que `exposedSessionsTotal`. */
  addressableInterestSessionsTotal: number | null;
  /** Masqué (`null`, Option C) dès que le total global est masqué OU qu'au moins un `topics[]` est individuellement masqué small-cohort, ou en cas de devises mêlées -- règle simple plutôt qu'une suppression complémentaire. */
  addressablePotentialValueTotal: number | null;
  topics: TopicDemandRow[];
}

@Injectable({
  providedIn: 'root'
})
export class PreventionGroupService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1/prevention/dashboard`;

  getDashboard(days: number): Observable<PreventionGroupDashboard> {
    return this.http.get<PreventionGroupDashboard>(this.baseUrl, { params: { days } });
  }

  /** `ageBand` optionnel -- omis du tout (jamais envoyé vide) quand "Tous les âges" est sélectionné. */
  getNeedsDashboard(days: number, ageBand: string | null): Observable<GroupNeedCoverageView> {
    const params: Record<string, string | number> = { days };
    if (ageBand) params['ageBand'] = ageBand;
    return this.http.get<GroupNeedCoverageView>(`${this.baseUrl}/needs`, { params });
  }

  /** Détail Need (Lot V2A.5-D) -- pas de filtre `ageBand` (segmentation reportée, contrat dédié futur). 404 (`HttpErrorResponse.status === 404`) si `needCode` est inconnu du catalogue. */
  getNeedDetail(needCode: string, days: number): Observable<NeedDetailView> {
    return this.http.get<NeedDetailView>(`${this.baseUrl}/needs/${encodeURIComponent(needCode)}`, { params: { days } });
  }

  /** Liste unifiée « Opportunités de prévention » (lot L6 / endpoint L5). `ageBand` omis (jamais envoyé vide) pour « Tous les âges ». Les méthodes `getNeedsDashboard`/`getNeedDetail` ci-dessus restent pour les autres consommateurs du contrat Need (pilotage groupe, carte site). */
  getOpportunities(days: number, ageBand: string | null): Observable<OpportunityDashboardView> {
    const params: Record<string, string | number> = { days };
    if (ageBand) params['ageBand'] = ageBand;
    return this.http.get<OpportunityDashboardView>(`${this.baseUrl}/opportunities`, { params });
  }

  /**
   * Détail d'un sujet unifié. `subjectKey` est OPAQUE : renvoyée exactement telle que reçue de la liste,
   * seulement encodée pour l'URL (`catalog:FATIGUE_CHECK` -> `catalog%3AFATIGUE_CHECK`) -- jamais parsée
   * ni reconstruite. Pas de filtre `ageBand` (comme le détail d'un Need). 404 si le sujet est inconnu.
   */
  getOpportunityDetail(subjectKey: string, days: number): Observable<OpportunityDetailView> {
    return this.http.get<OpportunityDetailView>(`${this.baseUrl}/opportunities/${encodeURIComponent(subjectKey)}`, { params: { days } });
  }

  /** `GET /api/v1/prevention/dashboard/demand` (Lot V2A.6-C) -- voir `PatientDemandDashboardView` (backend). Pas de filtre `ageBand` (jamais introduit pour Patient Demand, voir cadrage). */
  getDemandDashboard(days: number): Observable<PatientDemandDashboardView> {
    return this.http.get<PatientDemandDashboardView>(`${this.baseUrl}/demand`, { params: { days } });
  }
}

/** Site > Prévention -- même base de route que PreventionHandoffController côté backend, distincte des endpoints publics /public/v1/prevention/**. */
@Injectable({
  providedIn: 'root'
})
export class PreventionService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1/prevention/establishments`;

  getActivity(establishmentId: string, page: number, size: number): Observable<ActivityPage> {
    return this.http.get<ActivityPage>(`${this.baseUrl}/${establishmentId}/activity`, {
      params: { page, size }
    });
  }

  getPerformance(establishmentId: string): Observable<PreventionPerformance> {
    return this.http.get<PreventionPerformance>(`${this.baseUrl}/${establishmentId}/performance`);
  }

  getFunnel(establishmentId: string): Observable<PreventionFunnel> {
    return this.http.get<PreventionFunnel>(`${this.baseUrl}/${establishmentId}/funnel`);
  }

  getFunnelAnalytics(establishmentId: string, days: number): Observable<FunnelAnalytics> {
    return this.http.get<FunnelAnalytics>(`${this.baseUrl}/${establishmentId}/analytics/funnel`, {
      params: { days }
    });
  }

  getPerformanceAnalytics(establishmentId: string, days: number): Observable<PerformanceAnalytics> {
    return this.http.get<PerformanceAnalytics>(`${this.baseUrl}/${establishmentId}/analytics/performance`, {
      params: { days }
    });
  }

  getOfferPerformanceDetail(establishmentId: string, offerId: string, days: number): Observable<OfferPerformanceDetail> {
    return this.http.get<OfferPerformanceDetail>(`${this.baseUrl}/${establishmentId}/analytics/performance/offers/${offerId}`, {
      params: { days }
    });
  }

  /** Résumé Coverage compact pour le bloc "Couverture & offres" (Lot V2A.5) -- pas de paramètre ageBand (pas de segmentation cohorte au niveau site). */
  getNeedsCoverageSummary(establishmentId: string, days: number): Observable<SiteNeedCoverageSummaryView> {
    return this.http.get<SiteNeedCoverageSummaryView>(`${this.baseUrl}/${establishmentId}/analytics/needs`, {
      params: { days }
    });
  }

  /** Catalogue bundle-aware d'un établissement (Lot 9.0-E.E.G) -- `OrganizationOffer` uniquement, jamais `PreventionCategory`/`PreventionNeed`. */
  getEstablishmentCatalog(establishmentId: string): Observable<EstablishmentOfferCatalog[]> {
    return this.http.get<EstablishmentOfferCatalog[]>(`${this.baseUrl}/${establishmentId}/catalog/offers`);
  }

  /**
   * PUT mono-site (Lot 9.0-E.E.G) -- URL historique (`.../catalog/offers/{offerId}`, Lot 5) mais
   * délègue désormais à `OrganizationOfferService#applyAvailabilityChange`, la MÊME logique que le
   * batch multi-site `PUT /offers/{offerId}/availability` (E.E.F) : mêmes règles de validation (prix
   * obligatoire si actif, 0 valide, négatif refusé, offre archivée non activable), vérifié dans le
   * code backend avant d'écrire ce client. Réponse `CatalogOfferView` : ne porte pas `bilans[]` (la
   * composition ne change jamais depuis ce PUT) -- le composant appelant garde la valeur déjà chargée.
   */
  setEstablishmentOfferAvailability(
    establishmentId: string, offerId: string, request: SetEstablishmentOfferAvailabilityRequest
  ): Observable<EstablishmentOfferAvailabilityResult> {
    return this.http.put<EstablishmentOfferAvailabilityResult>(`${this.baseUrl}/${establishmentId}/catalog/offers/${offerId}`, request);
  }

  getContactConfig(establishmentId: string): Observable<EstablishmentContact> {
    return this.http.get<EstablishmentContact>(`${this.baseUrl}/${establishmentId}/contact-config`);
  }

  setContactConfig(establishmentId: string, patientContactRole: PatientContactRole | null, patientReturnToReception?: boolean,
                   patientIdentityOnSite?: PatientIdentityMode, identityAttestation?: boolean): Observable<EstablishmentContact> {
    const body: { patientContactRole: PatientContactRole | null; patientReturnToReception?: boolean; patientIdentityOnSite?: PatientIdentityMode; identityAttestation?: boolean } = { patientContactRole };
    if (identityAttestation) body.identityAttestation = true;
    if (patientReturnToReception !== undefined) body.patientReturnToReception = patientReturnToReception;
    if (patientIdentityOnSite !== undefined) body.patientIdentityOnSite = patientIdentityOnSite;
    return this.http.put<EstablishmentContact>(`${this.baseUrl}/${establishmentId}/contact-config`, body);
  }
}
